import {
  Collapse,
  IconButton,
  Link,
  TableCell,
  TableRow,
} from '@one-thd/sui-atomic-components';
import { ArrowDown, ArrowUp } from '@one-thd/sui-icons';
import { DateTime } from 'luxon';
import React, { Dispatch, Fragment, SetStateAction } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import { dummyOrderDetails } from '../../../mocks/order-details/dummyOrderDetails';
import { IOrderDetailsResponse } from '../../../models/order-details';
import { routeBuilder } from '../../../utils/general';
import { putAccountIdValueInSessionStorage } from '../../../utils/shared/accountIdValueStorage';
import { orderNumberFormat } from '../../shared/order-number-formatter/OrderNumberFormatter';
import { PHTableMenu as ODTableMenu } from '../table-view';
import { orderNumberParams } from '../util/shared';
import { ODTableExceptionBadge, ODTableStatusBadge } from './index';
import { ODTableOrder } from './ODInterfaces';
import { ODExpandedTableRow } from './ODExpandedTableRow';
import { TSelectedRow } from '../../../models/purchase-history/table';
import { IApplianceDeliveries } from '../../../models/common';

export const ODTableRow = ({
  order,
  selected,
  index,
  customerType,
  setSelected,
  orderLength,
}: {
  order: ODTableOrder;
  selected: TSelectedRow;
  index: number;
  customerType: string;
  setSelected: Dispatch<SetStateAction<TSelectedRow>>;
  orderLength: number;
}) => {
  // TODO: use/create setAlertState functionality for ORDER VIEW table, or use it from PH table
  const { setAlertState, orderHistoryData } = usePurchaseHistoryContext();
  const navigate = useNavigate();

  const handleRowClick = (id: number) => {
    setSelected((prevSelected: TSelectedRow) => {
      return {
        ...prevSelected,
        [id]: selected[id] === id ? null : id,
      };
    });
  };

  const orderParams = {
    orderNumbers: orderNumberParams(order),
    encryptedOrderNumbers: orderHistoryData?.encryptedOrderNumbers,
    salesDate: order.salesDate ?? '',
    storeNumber: order.storeNumber ?? 0,
    transactionId: order.transactionId ?? 0,
    orderOrigin: order.orderOrigin ?? order.detailsSource ?? '',
    registerNumber: order.registerNumber ?? 0,
    transactionType: order.transactionType ?? '',
    index,
    nickName: order.nickName ?? '',
    localDelivery: true,
    POJobName: order.POJobName,
  };

  const orderDetailsUrl = `/myaccount/order-details${routeBuilder(
    orderParams,
  )}`;
  const orderDetailsOrderNumber =
    new URLSearchParams(orderDetailsUrl.split('?')[1]).get('orderNumber') ?? '';

  return (
    <Fragment key={index}>
      <TableRow lastVisible={selected[index] === orderLength}>
        <TableCell padding="control">
          <IconButton
            onClick={() => handleRowClick(index)}
            icon={selected[index] === index ? ArrowUp : ArrowDown}
          />
        </TableCell>

        <TableCell variant="body" padding="control" style={{ width: '125px' }}>
          {DateTime.fromISO(order?.createDateTime ?? '').toFormat('LL/dd/yy')}
        </TableCell>

        <TableCell
          variant="body"
          padding="control"
          style={{
            width: '141px',
          }}
        >
          <Link
            variant="body-base"
            onClick={() => {
              putAccountIdValueInSessionStorage(
                orderDetailsOrderNumber,
                order.accountIdValue,
                order.accountIdType,
              );
              navigate(orderDetailsUrl);
            }}
          >
            {orderNumberFormat(
              order?.orderNumber,
              null, // TODO: add transactionId and registerNumber if model will have it otherwise keep null
              null,
              false,
            ).toUpperCase()}
          </Link>
        </TableCell>

        <TableCell variant="body" padding="control" style={{ width: '124px' }}>
          {order?.projectName ? order?.projectName : ''}
        </TableCell>

        <TableCell variant="body" padding="control" style={{ width: '124px' }}>
          {order?.POJobName ? order?.POJobName : ''}
        </TableCell>

        <TableCell variant="body" padding="control">
          <ODTableStatusBadge exceptionType={order?.statusDescription ?? ''} />
        </TableCell>

        <TableCell variant="body" padding="control">
          <ODTableExceptionBadge exceptions={order?.fulfillmentGroups ?? []} />
        </TableCell>

        {/* Possible to fix it */}
        {/* if fullfilmentGroup.length == 1 -> reservationStartDate else lenght > 1 Multiple */}
        {/* Needs to be checked with Sujatha! */}
        <TableCell variant="body" padding="control">
          {(() => {
            if ((order?.fulfillmentGroups?.length ?? 0) > 1) {
              return 'Multiple';
            }
            // BODFS
            const bodfsDelivery =
              order?.fulfillmentGroups?.[0]?.bodfsDeliveryAndHandling
                ?.bodfsDeliveries;

            if ((bodfsDelivery?.length ?? 0) === 1) {
              if (
                bodfsDelivery?.[0]?.reservationWindowStartTime?.split(
                  'T',
                )?.[0] ===
                bodfsDelivery?.[0]?.reservationWindowEndTime?.split('T')?.[0]
              ) {
                if (
                  bodfsDelivery?.[0]?.reservationWindowStartTime === undefined
                ) {
                  return '';
                }
                return DateTime.fromISO(
                  bodfsDelivery?.[0]?.reservationWindowStartTime ?? '',
                ).toFormat('LL/dd/yy');
              }
              return 'Multiple';
            }
            if ((bodfsDelivery?.length ?? 0) > 1) {
              return 'Multiple';
            }

            // Appliance
            const applianceDelivery = order?.fulfillmentGroups?.[0]
              ?.shippingAndHandling
              ?.applianceDeliveries as IApplianceDeliveries[];

            if ((applianceDelivery?.length ?? 0) === 1) {
              if (applianceDelivery?.[0]?.currentDeliveryDate === undefined) {
                return '';
              }
              return DateTime.fromISO(
                applianceDelivery?.[0]?.currentDeliveryDate ?? '',
              ).toFormat('LL/dd/yy');
            }
            if ((applianceDelivery?.length ?? 0) > 1) {
              return 'Multiple';
            }

            return '';
          })()}
        </TableCell>

        <TableCell>
          <ODTableMenu
            accountIdValue={order.accountIdValue}
            accountIdType={order.accountIdType}
            enableExportAndPrint={false}
            orderParams={orderParams}
            orderOrigin={
              (dummyOrderDetails as unknown as IOrderDetailsResponse)
                ?.orderOrigin ?? ''
            }
            customerType={customerType}
            orderNumber={order.orderNumber ?? order.orderNumbers?.[0] ?? ''}
            encryptedReceiptRequest={order.encryptedReceiptRequest ?? ''}
            setAlertState={setAlertState}
            orderDetailsData={
              dummyOrderDetails as unknown as IOrderDetailsResponse
            }
            receiptImageSting={
              (dummyOrderDetails as unknown as IOrderDetailsResponse)
                ?.receiptImageSting ?? ''
            }
            enableReceipt={false}
          />
        </TableCell>
      </TableRow>

      <TableRow collapsed={selected[index] !== index}>
        <TableCell
          class="sui-table-cell-base sui-bg-primary sui-text-left sui-pt-4 sui-pr-10 sui-pb-5 sui-pl-11"
          colSpan={12}
        >
          <Collapse in={selected[index] === index}>
            <ODExpandedTableRow localDeliveryOrder={order} />
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
};
